import React, { useState, useMemo } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/Card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/Table";
import { Button } from "../ui/Button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/Select";
import { formatNumber, formatDecimal, formatPercent, formatSignedNumber, formatCurrency, deltaTextClass } from "./format";
import { downloadSectionedXlsx } from "./xlsxExport";
import { fetchFinancialReviewSites, fetchFinancialReviewBreakdown } from "./api";
import { KpiCard, TablePager, usePagedFetch, usePagedRows } from "./ui";

function usePagedSites(filters) {
  return usePagedFetch(fetchFinancialReviewSites, filters, { enabled: Boolean(filters.report_month) });
}

function usePagedBreakdown(dimension, filters) {
  return usePagedFetch(fetchFinancialReviewBreakdown, { dimension, ...filters }, {
    enabled: Boolean(filters.report_month),
  });
}

// Always-visible table card for the four breakdown tables (By VISN/
// specialty/location/site), laid out side by side in a grid. Each card
// expands independently -- expanding one grows it to the full grid row
// width instead of squeezing every column into half the page, so wide
// tables like By location (ten columns) don't wrap or clip.
// By VISN, By specialty and By location share one row; only one of them
// can be expanded at a time so the expanded one can take over that whole
// row instead of leaving the other two stranded in a half-filled row.
const ROW_GROUP_KEYS = ["visn", "specialty", "location"];

function ExpandableTableCard({ title, description, headerExtra, expanded, onToggleExpand, children }) {
  return (
    <Card className={expanded ? "lg:col-span-full" : undefined}>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="min-w-0 flex-1">
          <CardTitle className="text-base">{title}</CardTitle>
          <div className="flex items-center gap-3 mt-1">
            {description && <CardDescription className="truncate">{description}</CardDescription>}
            {headerExtra}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onToggleExpand} className="shrink-0 gap-1">
          {expanded ? (
            <>
              <Minimize2 className="h-4 w-4" /> Collapse
            </>
          ) : (
            <>
              <Maximize2 className="h-4 w-4" /> Expand
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

export default function FinancialReviewTab({ data, loading, error }) {
  // Hooks must run unconditionally, in the same order, on every render (the
  // Rules of Hooks) -- so these three pagers have to sit above the
  // loading/error/no-data early returns below, not after them. `data` can
  // still be null/undefined here (during the initial load, or on an error),
  // hence the `|| []` fallbacks -- usePagedRows always gets an array.
  const visnPager = usePagedBreakdown("visn", {
    report_month: data?.report_month,
    visn: data?.filters?.visn,
    station_id: data?.filters?.station_id,
    specialty: data?.filters?.specialty,
    state: data?.filters?.state,
    new_visn: data?.filters?.new_visn,
  });
  const specialtyPager = usePagedBreakdown("specialty", {
    report_month: data?.report_month,
    visn: data?.filters?.visn,
    station_id: data?.filters?.station_id,
    specialty: data?.filters?.specialty,
    state: data?.filters?.state,
    new_visn: data?.filters?.new_visn,
  });
  const statePager = usePagedBreakdown("location", {
    report_month: data?.report_month,
    visn: data?.filters?.visn,
    station_id: data?.filters?.station_id,
    specialty: data?.filters?.specialty,
    state: data?.filters?.state,
    new_visn: data?.filters?.new_visn,
  });
  // Drill-down under By location: "" = no state picked yet (city table
  // hidden). Filtering + pagination happens entirely client-side over the
  // already-fetched by_city rows -- no extra request when the selection
  // changes.
  const [cityState, setCityState] = useState("");
  // Memoized so the array reference only changes when by_city or the
  // selected state actually changes -- otherwise a fresh .filter() result
  // on every render would look like "new rows" to usePagedRows below and
  // reset the page back to 1 right after every click on Next.
  const cityRowsForState = useMemo(
    () => (data?.by_city || []).filter((row) => row.state === cityState),
    [data?.by_city, cityState]
  );
  const cityPager = usePagedRows(cityRowsForState, { pageSize: 5 });

  // By site's own data source (see usePagedSites above) -- keyed off the
  // *resolved* report_month/filters the main report settled on, so this
  // stays in lockstep with everything else on the page without needing its
  // own copy of the filter bar's state.
  const sites = usePagedSites({
    report_month: data?.report_month,
    visn: data?.filters?.visn,
    station_id: data?.filters?.station_id,
    specialty: data?.filters?.specialty,
    state: data?.filters?.state,
    new_visn: data?.filters?.new_visn,
  });

  // Which breakdown cards (visn/specialty/location/site) are expanded to
  // full grid width -- keyed independently so more than one can be
  // expanded at once.
  const [expandedCards, setExpandedCards] = useState({});
  const toggleExpand = (key) =>
    setExpandedCards((prev) => {
      const next = !prev[key];
      if (ROW_GROUP_KEYS.includes(key)) {
        const cleared = Object.fromEntries(ROW_GROUP_KEYS.map((k) => [k, false]));
        return { ...prev, ...cleared, [key]: next };
      }
      return { ...prev, [key]: next };
    });

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading Financial Review report…</p>;
  }
  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }
  if (!data) return null;

  const { summary, by_visn, by_specialty, by_state, by_city, by_station, report_month, previous_report_month, revenue } = data;

  // Distinct states to offer in the By location city picker -- derived from
  // by_state (already one row per state) rather than a separate fetch.
  const cityStateOptions = by_state
    .map((row) => row.state)
    .filter(Boolean)
    .sort();

  const handleDownloadXlsx = () => {
    downloadSectionedXlsx(`financial-review-${report_month || "report"}.xlsx`, "Financial Review", [
      {
        title: "Summary",
        columns: [
          { header: "Report Month", key: "report_month" },
          { header: "Previous Month", key: "previous_report_month" },
          { header: "Licensed", key: "licensed" },
          { header: "Launched", key: "activated" },
          { header: "Launch Rate", key: "license_to_launched_rate", format: "percent" },
          { header: "Active Users", key: "active_users" },
          { header: "Recordings", key: "recordings" },
          { header: "Avg Daily Active Users", key: "avg_dau" },
          { header: "MoM Licensed Delta", key: "mom_licensed_delta" },
          { header: "Estimated Revenue", key: "revenue", format: "currency" },
        ],
        rows: [
          {
            report_month,
            previous_report_month,
            licensed: summary.licensed,
            activated: summary.activated,
            license_to_launched_rate: summary.license_to_launched_rate,
            active_users: summary.active_users,
            recordings: summary.recordings,
            avg_dau: summary.avg_dau,
            mom_licensed_delta: summary.mom_licensed_delta,
            revenue,
          },
        ],
      },
      {
        title: "By VISN",
        columns: [
          { header: "VISN", key: "visn" },
          { header: "RISE VISN", key: "new_visn" },
          { header: "Licensed", key: "licensed" },
          { header: "Launched", key: "activated" },
          { header: "Revenue", key: "revenue", format: "currency" },
          { header: "Idle 14+ Days", key: "idle_14d" },
          { header: "Potential Lost Revenue", key: "potential_lost_revenue", format: "currency" },
        ],
        rows: by_visn,
      },
      {
        title: "By Specialty",
        columns: [
          { header: "Specialty", key: "specialty" },
          { header: "Licensed", key: "licensed" },
          { header: "Launched", key: "activated" },
          { header: "Revenue", key: "revenue", format: "currency" },
          { header: "Idle 14+ Days", key: "idle_14d" },
          { header: "Potential Lost Revenue", key: "potential_lost_revenue", format: "currency" },
        ],
        rows: by_specialty,
      },
      {
        title: "By State",
        columns: [
          { header: "State", key: "state" },
          { header: "Licensed", key: "licensed" },
          { header: "Launched", key: "activated" },
          { header: "Revenue", key: "revenue", format: "currency" },
          { header: "Idle 14+ Days", key: "idle_14d" },
          { header: "Potential Lost Revenue", key: "potential_lost_revenue", format: "currency" },
          { header: "Sites", key: "site_count" },
          { header: "Cities", key: "city_count" },
          { header: "Organizations", key: "organization_count" },
        ],
        rows: by_state,
      },
      {
        title: "By City",
        columns: [
          { header: "State", key: "state" },
          { header: "City", key: "city" },
          { header: "Licensed", key: "licensed" },
          { header: "Launched", key: "activated" },
          { header: "Revenue", key: "revenue", format: "currency" },
          { header: "Idle 14+ Days", key: "idle_14d" },
          { header: "Potential Lost Revenue", key: "potential_lost_revenue", format: "currency" },
          { header: "Sites", key: "site_count" },
          { header: "Organizations", key: "organization_count" },
        ],
        rows: by_city,
      },
      {
        title: "Top Sites (top 25 for the selected filters, not the full list)",
        columns: [
          { header: "Site", key: "site_name" },
          { header: "Station ID", key: "station_id" },
          { header: "VISN", key: "visn" },
          { header: "RISE VISN", key: "new_visn" },
          { header: "Organization", key: "organization" },
          { header: "Licensed", key: "licensed" },
          { header: "Launched", key: "activated" },
        ],
        rows: by_station,
      },
    ]);
  };

  // Which of the three row-group cards (if any) is currently expanded --
  // drives whether they render as one 3-across row or as an expanded card
  // on its own full-width row with the other two sharing the row below it.
  const expandedRowKey = ROW_GROUP_KEYS.find((key) => expandedCards[key]);

  // Distinct RISE VISNs represented across the matching legacy VISNs --
  // read off the full (unpaginated) by_visn list rather than visnPager's
  // current page, since a page-local count would undercount once By VISN
  // spans more than one page.
  const riseVisnCount = new Set(by_visn.map((row) => row.new_visn).filter((v) => v != null)).size;

  const visnCard = (
        <ExpandableTableCard
          title="By VISN"
          description={
            visnPager.total
              ? `${visnPager.total} VISNs match the current filters, spanning ${riseVisnCount} RISE VISN${
                  riseVisnCount === 1 ? "" : "s"
                }.`
              : ""
          }
          expanded={!!expandedCards.visn}
          onToggleExpand={() => toggleExpand("visn")}
        >
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">VISN</TableHead>
                <TableHead className="whitespace-nowrap">RISE VISN</TableHead>
                <TableHead>Licensed</TableHead>
                <TableHead>Launched</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>Idle 14+ days</TableHead>
                <TableHead>Potential lost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visnPager.rows.map((row) => (
                <TableRow key={row.visn}>
                  <TableCell className="whitespace-nowrap">VISN {row.visn}</TableCell>
                  <TableCell className="whitespace-nowrap">{row.new_visn != null ? `VISN ${row.new_visn}` : "—"}</TableCell>
                  <TableCell>{formatNumber(row.licensed)}</TableCell>
                  <TableCell>{formatNumber(row.activated)}</TableCell>
                  <TableCell>{formatCurrency(row.revenue)}</TableCell>
                  <TableCell>{formatNumber(row.idle_14d)}</TableCell>
                  <TableCell className="text-red-600">{formatCurrency(row.potential_lost_revenue)}</TableCell>
                </TableRow>
              ))}
              {!visnPager.loading && visnPager.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No VISNs found for the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
          {visnPager.loading && <p className="text-xs text-muted-foreground text-center py-3">Loading…</p>}
          {visnPager.error && <p className="text-xs text-red-600 text-center py-3">{visnPager.error}</p>}
          <TablePager page={visnPager.page} totalPages={visnPager.totalPages} onPageChange={visnPager.setPage} />
        </ExpandableTableCard>
  );

  const specialtyCard = (
        <ExpandableTableCard
          title="By specialty"
          description={specialtyPager.total ? `${specialtyPager.total} specialties match the current filters.` : ""}
          expanded={!!expandedCards.specialty}
          onToggleExpand={() => toggleExpand("specialty")}
        >
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Specialty</TableHead>
                <TableHead>Licensed</TableHead>
                <TableHead>Launched</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>Idle 14+ days</TableHead>
                <TableHead>Potential lost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {specialtyPager.rows.map((row) => (
                <TableRow key={row.specialty}>
                  <TableCell>{row.specialty}</TableCell>
                  <TableCell>{formatNumber(row.licensed)}</TableCell>
                  <TableCell>{formatNumber(row.activated)}</TableCell>
                  <TableCell>{formatCurrency(row.revenue)}</TableCell>
                  <TableCell>{formatNumber(row.idle_14d)}</TableCell>
                  <TableCell className="text-red-600">{formatCurrency(row.potential_lost_revenue)}</TableCell>
                </TableRow>
              ))}
              {!specialtyPager.loading && specialtyPager.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No specialties found for the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
          {specialtyPager.loading && <p className="text-xs text-muted-foreground text-center py-3">Loading…</p>}
          {specialtyPager.error && <p className="text-xs text-red-600 text-center py-3">{specialtyPager.error}</p>}
          <TablePager page={specialtyPager.page} totalPages={specialtyPager.totalPages} onPageChange={specialtyPager.setPage} />
        </ExpandableTableCard>
  );

  const locationCard = (
        <ExpandableTableCard
          title="By location"
          description={
            cityState
              ? cityRowsForState.length
                ? `${cityRowsForState.length} cities in ${cityState} match the current filters.`
                : ""
              : statePager.total
              ? `${statePager.total} states match the current filters.`
              : ""
          }
          headerExtra={
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground whitespace-nowrap">See cities in:</span>
              <Select value={cityState} onValueChange={setCityState}>
                <SelectTrigger className="h-7 w-[140px] text-xs">
                  <SelectValue placeholder="Select a state" />
                </SelectTrigger>
                <SelectContent>
                  {cityStateOptions.map((st) => (
                    <SelectItem key={st} value={st}>
                      {st}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {cityState && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setCityState("")}>
                  Back to all states
                </Button>
              )}
            </div>
          }
          expanded={!!expandedCards.location}
          onToggleExpand={() => toggleExpand("location")}
        >
          {cityState ? (
            <>
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>City</TableHead>
                    <TableHead>Licensed</TableHead>
                    <TableHead>Launched</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Idle 14+ days</TableHead>
                    <TableHead>Potential lost</TableHead>
                    <TableHead>Sites</TableHead>
                    <TableHead>Organizations</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cityPager.pageRows.map((row) => (
                    <TableRow key={row.city || "unknown"}>
                      <TableCell>{row.city || "—"}</TableCell>
                      <TableCell>{formatNumber(row.licensed)}</TableCell>
                      <TableCell>{formatNumber(row.activated)}</TableCell>
                      <TableCell>{formatCurrency(row.revenue)}</TableCell>
                      <TableCell>{formatNumber(row.idle_14d)}</TableCell>
                      <TableCell className="text-red-600">{formatCurrency(row.potential_lost_revenue)}</TableCell>
                      <TableCell>{formatNumber(row.site_count)}</TableCell>
                      <TableCell>{formatNumber(row.organization_count)}</TableCell>
                    </TableRow>
                  ))}
                  {cityRowsForState.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground">
                        No cities found for {cityState} under the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
              <TablePager page={cityPager.page} totalPages={cityPager.totalPages} onPageChange={cityPager.setPage} />
            </>
          ) : (
            <>
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>State</TableHead>
                    <TableHead>Licensed</TableHead>
                    <TableHead>Launched</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Idle 14+ days</TableHead>
                    <TableHead>Potential lost</TableHead>
                    <TableHead>Sites</TableHead>
                    <TableHead>Cities</TableHead>
                    <TableHead>Organizations</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statePager.rows.map((row) => (
                    <TableRow key={row.state || "unknown"}>
                      <TableCell>{row.state || "—"}</TableCell>
                      <TableCell>{formatNumber(row.licensed)}</TableCell>
                      <TableCell>{formatNumber(row.activated)}</TableCell>
                      <TableCell>{formatCurrency(row.revenue)}</TableCell>
                      <TableCell>{formatNumber(row.idle_14d)}</TableCell>
                      <TableCell className="text-red-600">{formatCurrency(row.potential_lost_revenue)}</TableCell>
                      <TableCell>{formatNumber(row.site_count)}</TableCell>
                      <TableCell>{formatNumber(row.city_count)}</TableCell>
                      <TableCell>{formatNumber(row.organization_count)}</TableCell>
                    </TableRow>
                  ))}
                  {!statePager.loading && statePager.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground">
                        No states found for the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
              {statePager.loading && <p className="text-xs text-muted-foreground text-center py-3">Loading…</p>}
              {statePager.error && <p className="text-xs text-red-600 text-center py-3">{statePager.error}</p>}
              <TablePager page={statePager.page} totalPages={statePager.totalPages} onPageChange={statePager.setPage} />
            </>
          )}
        </ExpandableTableCard>
  );

  const rowCards = { visn: visnCard, specialty: specialtyCard, location: locationCard };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Financial Review</h2>
          <p className="text-sm text-muted-foreground">
            {report_month} — matches the KAROLINE dashboard's Licensed/Launched/Active numbers for these filters, using
            the same month-over-month snapshot logic as the KAROLINE dashboard.
          </p>
        </div>
        <Button variant="outline" onClick={handleDownloadXlsx}>
          Download XLSX
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <KpiCard label="Licensed" value={formatNumber(summary.licensed)} />
        <KpiCard
          label="Launched"
          value={formatNumber(summary.activated)}
          sublabel={`${formatPercent(summary.license_to_launched_rate)} of licensed`}
        />
        <KpiCard
          label="Active users"
          value={formatNumber(summary.active_users)}
          sublabel={`Recorded at least once in ${report_month}`}
        />
        <KpiCard label="Recordings" value={formatNumber(summary.recordings)} sublabel={report_month} />
        <KpiCard label="Avg daily active users" value={formatDecimal(summary.avg_dau)} sublabel={report_month} />
        <KpiCard
          label={`Licensed vs. ${previous_report_month}`}
          value={
            <span className={deltaTextClass(summary.mom_licensed_delta)}>
              {formatSignedNumber(summary.mom_licensed_delta)}
            </span>
          }
        />
        <KpiCard
          label="Estimated revenue"
          value={formatCurrency(revenue)}
          sublabel={`${formatCurrency(150)}/mo per licensed user`}
        />
      </div>

      {expandedRowKey ? (
        <>
          <div>{rowCards[expandedRowKey]}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {ROW_GROUP_KEYS.filter((key) => key !== expandedRowKey).map((key) => (
              <React.Fragment key={key}>{rowCards[key]}</React.Fragment>
            ))}
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {visnCard}
          {specialtyCard}
          {locationCard}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        <ExpandableTableCard
          title="By site"
          description={sites.total ? `${sites.total} sites match the current filters.` : ""}
          expanded={!!expandedCards.site}
          onToggleExpand={() => toggleExpand("site")}
        >
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Site</TableHead>
                <TableHead>VISN</TableHead>
                <TableHead className="whitespace-nowrap">RISE VISN</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Licensed</TableHead>
                <TableHead>Launched</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.rows.map((row) => (
                <TableRow key={row.station_id}>
                  <TableCell>{row.site_name ? `${row.site_name} (${row.station_id})` : row.station_id}</TableCell>
                  <TableCell>{row.visn != null ? `VISN ${row.visn}` : "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">{row.new_visn != null ? `VISN ${row.new_visn}` : "—"}</TableCell>
                  <TableCell>{row.organization || "—"}</TableCell>
                  <TableCell>{formatNumber(row.licensed)}</TableCell>
                  <TableCell>{formatNumber(row.activated)}</TableCell>
                </TableRow>
              ))}
              {!sites.loading && sites.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No sites found for the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>

          {sites.loading && (
            <p className="text-xs text-muted-foreground text-center py-3">Loading sites…</p>
          )}
          {sites.error && (
            <p className="text-xs text-red-600 text-center py-3">{sites.error}</p>
          )}
          <TablePager page={sites.page} totalPages={sites.totalPages} onPageChange={sites.setPage} />
        </ExpandableTableCard>
      </div>
    </div>
  );
}
